import { createAccessControl } from "better-auth/plugins/access"
import { defaultStatements, adminAc, ownerAc } from "better-auth/plugins/organization/access"

// Extend Better Auth's default org statements with dealer platform resources
const statements = {
  ...defaultStatements,
  settings: ["view", "update"],
  users: ["view", "invite", "update", "remove"],
  billing: ["view", "manage"],
  inventory: ["view", "sync", "configure"],
  analytics: ["view", "export"],
  auditLogs: ["view"],
  featureFlags: ["view", "toggle"],
  documents: ["view", "create", "update", "delete", "send"],
  seo: ["view", "audit", "configure"],
  website: ["view", "edit", "publish"],
  discussions: ["view", "create", "moderate"],
  stores: ["view", "create", "update", "delete"],
} as const

export const ac = createAccessControl(statements)

// --- Role definitions ---

export const owner = ac.newRole({
  ...ownerAc.statements,
  settings: ["view", "update"],
  users: ["view", "invite", "update", "remove"],
  billing: ["view", "manage"],
  inventory: ["view", "sync", "configure"],
  analytics: ["view", "export"],
  auditLogs: ["view"],
  featureFlags: ["view", "toggle"],
  documents: ["view", "create", "update", "delete", "send"],
  seo: ["view", "audit", "configure"],
  website: ["view", "edit", "publish"],
  discussions: ["view", "create", "moderate"],
  stores: ["view", "create", "update", "delete"],
})

export const admin = ac.newRole({
  ...adminAc.statements,
  settings: ["view", "update"],
  users: ["view", "invite", "update"],
  billing: ["view"],
  inventory: ["view", "sync", "configure"],
  analytics: ["view", "export"],
  auditLogs: ["view"],
  featureFlags: ["view"],
  documents: ["view", "create", "update", "delete", "send"],
  seo: ["view", "audit", "configure"],
  website: ["view", "edit", "publish"],
  discussions: ["view", "create", "moderate"],
  stores: ["view", "create", "update"],
})

export const manager = ac.newRole({
  settings: ["view"],
  users: ["view"],
  inventory: ["view", "sync"],
  analytics: ["view"],
  documents: ["view", "create", "update", "delete"],
  seo: ["view", "audit"],
  website: ["view", "edit"],
  discussions: ["view", "create"],
  stores: ["view"],
})

export const member = ac.newRole({
  settings: ["view"],
  users: ["view"],
  inventory: ["view"],
  analytics: ["view"],
  documents: ["view"],
  seo: ["view"],
  website: ["view"],
  discussions: ["view", "create"],
  stores: ["view"],
})

// --- Types ---

export type Resource = keyof typeof statements
export type Action<R extends Resource> = (typeof statements)[R][number]

// Static role names for permission checks without Better Auth API calls
export const staticRoles = { owner, admin, manager, member } as const
export type RoleName = keyof typeof staticRoles

// Quick lookup: resource+action → which static roles allow it
const _rolePermissionCache = new Map<string, Set<RoleName>>()

function buildRolePermissionCache() {
  const roleEntries = Object.entries(staticRoles) as [RoleName, (typeof staticRoles)[RoleName]][]
  for (const [roleName, role] of roleEntries) {
    const stmts = role.statements as Record<string, readonly string[]>
    for (const [resource, actions] of Object.entries(stmts)) {
      for (const action of actions) {
        const key = `${resource}:${action}`
        let set = _rolePermissionCache.get(key)
        if (!set) {
          set = new Set()
          _rolePermissionCache.set(key, set)
        }
        set.add(roleName)
      }
    }
  }
}
buildRolePermissionCache()

/** Check if a static role has a given permission (no network call). */
export function hasStaticPermission(role: string, resource: Resource, action: string): boolean {
  const key = `${resource}:${action}`
  const allowed = _rolePermissionCache.get(key)
  return !!allowed && allowed.has(role as RoleName)
}
